import slotCategories from 'enums/slot_categories.json';
import wellStatusColors from 'enums/well_status_colors.json';
import wellStatusTypes from 'enums/well_status_types.json';

/**
 * The slot categories, in display order (app/shared/enums/slot_categories.json, read by Ruby's
 * SlotCategory too). Every table, chart, legend and filter that lists categories reads this
 * list, so a category added to the file reaches all of them.
 *
 * A JSON import types its keys as plain strings, so the union is spelled out here; the spec
 * beside this pins it to the file.
 */
export type SlotCategoryKey =
  | 'pdp'
  | 'duc'
  | 'permit'
  | 'proposal'
  | 'planned'
  | 'pre_permit'
  | 'apd'
  | 'ltd';

type Entry = {
  key: string;
  label?: string;
  color?: string;
  wellStatus?: boolean;
};

const ENTRIES = slotCategories as Entry[];

/** Every category key, in display order. */
export const SLOT_CATEGORY_KEYS = ENTRIES.map(
  (entry) => entry.key
) as readonly SlotCategoryKey[];

/** The Create New Well statuses that stand as their own categories, in display order. */
export const WELL_STATUS_SLOT_CATEGORY_KEYS = ENTRIES.filter(
  (entry) => entry.wellStatus
).map((entry) => entry.key) as readonly SlotCategoryKey[];

/** Category => label. A Create New Well status keeps its well-status label. */
export const SLOT_CATEGORY_LABELS = Object.fromEntries(
  ENTRIES.map((entry) => [
    entry.key,
    entry.label ??
      (wellStatusTypes as Record<string, string>)[entry.key] ??
      entry.key,
  ])
) as Readonly<Record<SlotCategoryKey, string>>;

/** Category => chart colour. A Create New Well status keeps its well-status colour. */
export const SLOT_CATEGORY_COLORS = Object.fromEntries(
  ENTRIES.map((entry) => [
    entry.key,
    entry.color ?? (wellStatusColors as Record<string, string>)[entry.key],
  ])
) as Readonly<Record<SlotCategoryKey, string>>;

/**
 * Whether a value is a slot category.
 *
 * @param {string} value - A category key from a response.
 * @return {boolean} True when the shared list names it.
 */
export const isSlotCategory = (value: string): value is SlotCategoryKey =>
  (SLOT_CATEGORY_KEYS as readonly string[]).includes(value);

/**
 * A Create New Well status's key in a counts response (slot summary rows, formation table, the
 * Allocation Manager, wellCount, the DPS capture panel): the status camelCased, so `pre_permit`
 * is `prePermit`.
 */
export type WellStatusCountKey = 'proposal' | 'planned' | 'prePermit' | 'apd';

/**
 * @param {string} status - A stored status (`pre_permit`).
 * @return {string} Its counts-response key (`prePermit`).
 */
export const wellStatusCountKey = (status: string): string =>
  status.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase());

/** One column per Create New Well status, in display order: its counts key, status and label. */
export const WELL_STATUS_COUNT_COLUMNS: readonly {
  key: WellStatusCountKey;
  status: SlotCategoryKey;
  label: string;
}[] = WELL_STATUS_SLOT_CATEGORY_KEYS.map((status) => ({
  key: wellStatusCountKey(status) as WellStatusCountKey,
  status,
  label: SLOT_CATEGORY_LABELS[status],
}));

/** The counts-response keys of the Create New Well statuses, in display order. */
export const WELL_STATUS_COUNT_KEYS: readonly WellStatusCountKey[] =
  WELL_STATUS_COUNT_COLUMNS.map((column) => column.key);

/** A count per Create New Well status. */
export type WellStatusCounts = Record<WellStatusCountKey, number>;

/**
 * @param {Partial<WellStatusCounts> | undefined} counts - Counts from a response, any missing.
 * @return {WellStatusCounts} Every status's count, 0 where absent.
 */
export const wellStatusCounts = (
  counts?: Partial<WellStatusCounts> | null
): WellStatusCounts =>
  Object.fromEntries(
    WELL_STATUS_COUNT_KEYS.map((key) => [key, counts?.[key] ?? 0])
  ) as WellStatusCounts;

/**
 * A value per slot category, keyed in display order — for visibility toggles, colours and sums.
 *
 * @param {Function} value - The value for one category.
 * @return {Record<SlotCategoryKey, T>} One entry per category.
 */
export const slotCategoryRecord = <T>(
  value: (key: SlotCategoryKey) => T
): Record<SlotCategoryKey, T> =>
  Object.fromEntries(
    SLOT_CATEGORY_KEYS.map((key) => [key, value(key)])
  ) as Record<SlotCategoryKey, T>;

/**
 * The categories bottom-to-top as a stacked chart draws them: the reverse of display order, so
 * the first category (PDP) sits at the base of each column and LTD on top.
 */
export const SLOT_CATEGORY_STACK_ORDER: readonly SlotCategoryKey[] = [
  ...SLOT_CATEGORY_KEYS,
].reverse();

/**
 * The categories a result screen shows, in display order: PDP, DUC, Permit and LTD always, and a
 * Create New Well status only when `present` says the run's own data has it (INS-13303). The four
 * are held unvalued until PRE_PERMIT_VALUATION_ENABLED is on, so most runs carry none of them and
 * show no empty column, legend entry or row for them; a run that did value them shows them.
 *
 * @param {Function} present - Whether the run's data has the category.
 * @return {SlotCategoryKey[]} The categories to show.
 */
export const shownSlotCategories = (
  present: (key: SlotCategoryKey) => boolean
): SlotCategoryKey[] =>
  SLOT_CATEGORY_KEYS.filter(
    (key) => !WELL_STATUS_SLOT_CATEGORY_KEYS.includes(key) || present(key)
  );

/**
 * shownSlotCategories over rows that each carry a category and a value: a Create New Well
 * status shows once one of its rows is non-zero.
 *
 * @param {T[]} rows - The run's rows.
 * @param {Function} categoryOf - A row's category.
 * @param {Function} valueOf - A row's value.
 * @return {SlotCategoryKey[]} The categories to show.
 */
export const shownSlotCategoriesIn = <T>(
  rows: readonly T[],
  categoryOf: (row: T) => string,
  valueOf: (row: T) => number | null | undefined
): SlotCategoryKey[] => {
  const present = new Set(
    rows.filter((row) => (valueOf(row) ?? 0) !== 0).map(categoryOf)
  );
  return shownSlotCategories((key) => present.has(key));
};

/** The fields of a package-overview pricing row that say a category was priced. */
type PricedRow = {
  type: string;
  nra?: number;
  includedNpv?: number;
  excludedNpv?: number;
};

/**
 * shownSlotCategories over a package overview's pricing sections: a Create New Well status shows
 * once any section prices it (non-zero NRA or NPV).
 *
 * @param {Array} sections - The overview's pricing sections (target, max, mtm).
 * @return {SlotCategoryKey[]} The categories to show.
 */
export const shownPricingSlotCategories = (
  sections: readonly (readonly PricedRow[] | undefined)[]
): SlotCategoryKey[] =>
  shownSlotCategoriesIn(
    sections.flatMap((rows) => rows ?? []),
    (row) => row.type,
    (row) =>
      Math.abs(row.nra ?? 0) +
      Math.abs(row.includedNpv ?? 0) +
      Math.abs(row.excludedNpv ?? 0)
  );
